import { FBTrackingEvent } from '../FBTrackingEvent';
import { IGameState } from '../../../State/Game/IGameState';
import { getHighestLevelCompleted } from '../../../State/Player/PlayerLevelHelper';
import Game from '../../../Game';
import { PlayerStateHelper } from '../../../State/Player/PlayerStateHelper';
import { TimerType } from '../../../System/Timer/TimerTypes';
import DateHelper from '../../DateHelper';
import { FBPixel } from '../../SDK/FBPixelHandler';

// If account is older than this number, don't bother tracking
const MAX_DAYS_TO_TRACK: number = 7;

// Events that are specifically conditional
export type FBConditionalEvents =
    | FBTrackingEvent.AD_10_3DAYS
    | FBTrackingEvent.AD_20_5DAYS
    | FBTrackingEvent.AD_30_7DAYS
    | FBTrackingEvent.SESSION_5_3DAYS
    | FBTrackingEvent.SESSION_8_5DAYS
    | FBTrackingEvent.SESSION_11_7DAYS
    | FBTrackingEvent.LVL_20_3DAYS
    | FBTrackingEvent.LVL_40_5DAYS
    | FBTrackingEvent.LVL_60_7DAYS
    | FBTrackingEvent.SESSION_5_1DAY
    | FBTrackingEvent.SESSION_5_2DAYS
    | FBTrackingEvent.SESSION_5_5DAYS
    | FBTrackingEvent.SESSION_5_7DAYS
    | FBTrackingEvent.SESSION_4_1DAY
    | FBTrackingEvent.SESSION_4_2DAYS
    | FBTrackingEvent.SESSION_4_3DAYS
    | FBTrackingEvent.SESSION_3_1DAY
    | FBTrackingEvent.SESSION_3_2DAYS
    | FBTrackingEvent.SESSION_3_3DAYS
    | FBTrackingEvent.SESSION_2_1DAY
    | FBTrackingEvent.SESSION_2_2DAYS
    | FBTrackingEvent.SESSION_2_3DAYS
    | FBTrackingEvent.LVL_20_1DAY
    | FBTrackingEvent.LVL_20_2DAYS
    | FBTrackingEvent.LVL_20_5DAYS
    | FBTrackingEvent.LVL_20_7DAYS
    | FBTrackingEvent.LVL_10_1DAY
    | FBTrackingEvent.LVL_10_2DAYS
    | FBTrackingEvent.LVL_10_3DAYS
    | FBTrackingEvent.LVL_15_1DAY
    | FBTrackingEvent.LVL_15_2DAYS
    | FBTrackingEvent.LVL_15_3DAYS;

export enum FBTrackingMethod {
    DEFAULT = 'default',
    PIXEL = 'pixel',
    ALL = 'all',
}

interface IFBConditionalEventData {
    requiredCount: number;
    timespan?: number; // in days
    trackingMethod?: FBTrackingMethod;
    condition: (state: IGameState, data: IFBConditionalEventData) => boolean;
}

const FBConditionalEventData: Record<
    FBConditionalEvents,
    IFBConditionalEventData
> = {
    [FBTrackingEvent.AD_10_3DAYS]: {
        requiredCount: 10,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.progress.adsWatched === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.AD_20_5DAYS]: {
        requiredCount: 20,
        timespan: 5,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.progress.adsWatched === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.AD_30_7DAYS]: {
        requiredCount: 30,
        timespan: 7,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.progress.adsWatched === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_20_3DAYS]: {
        requiredCount: 20,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_40_5DAYS]: {
        requiredCount: 40,
        timespan: 5,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_60_7DAYS]: {
        requiredCount: 60,
        timespan: 7,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_5_3DAYS]: {
        requiredCount: 5,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_8_5DAYS]: {
        requiredCount: 8,
        timespan: 5,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_11_7DAYS]: {
        requiredCount: 11,
        timespan: 7,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_2_1DAY]: {
        requiredCount: 2,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_2_2DAYS]: {
        requiredCount: 2,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_2_3DAYS]: {
        requiredCount: 2,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_3_1DAY]: {
        requiredCount: 3,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_3_2DAYS]: {
        requiredCount: 3,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_3_3DAYS]: {
        requiredCount: 3,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_4_1DAY]: {
        requiredCount: 4,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_4_2DAYS]: {
        requiredCount: 4,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_4_3DAYS]: {
        requiredCount: 4,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_5_1DAY]: {
        requiredCount: 5,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_5_2DAYS]: {
        requiredCount: 5,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_5_5DAYS]: {
        requiredCount: 5,
        timespan: 5,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.SESSION_5_7DAYS]: {
        requiredCount: 5,
        timespan: 7,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                state.playerState.totalSessionCount === evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_10_1DAY]: {
        requiredCount: 10,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_10_2DAYS]: {
        requiredCount: 10,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_10_3DAYS]: {
        requiredCount: 10,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_15_1DAY]: {
        requiredCount: 15,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_15_2DAYS]: {
        requiredCount: 15,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_15_3DAYS]: {
        requiredCount: 15,
        timespan: 3,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_20_1DAY]: {
        requiredCount: 20,
        timespan: 1,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_20_2DAYS]: {
        requiredCount: 20,
        timespan: 2,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_20_5DAYS]: {
        requiredCount: 20,
        timespan: 5,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
    [FBTrackingEvent.LVL_20_7DAYS]: {
        requiredCount: 20,
        timespan: 7,
        trackingMethod: FBTrackingMethod.ALL,
        condition: (state: IGameState, evData: IFBConditionalEventData) => {
            return (
                DateHelper.timeSince(
                    state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                ) <=
                    DateHelper.Day * MAX_DAYS_TO_TRACK &&
                getHighestLevelCompleted(state.playerState) ===
                    evData.requiredCount
            );
        },
    },
};

export class ConditionalTrackingHelper {
    public static trackConditionally(
        state: IGameState,
        eventIDs: FBConditionalEvents | FBConditionalEvents[],
        trackOnce: boolean = true
    ): IGameState {
        // convert to array if needed
        if (!Array.isArray(eventIDs)) {
            eventIDs = [eventIDs];
        }

        // iterate over eventIDs
        for (let i = 0; i < eventIDs.length; i++) {
            const eventData: IFBConditionalEventData =
                FBConditionalEventData[eventIDs[i]];

            // if is older than timespan, or condition is false, skip it
            if (
                (eventData.timespan
                    ? DateHelper.timeSince(
                          state.playerState.timers.get(TimerType.INITIAL_LOGIN)
                      ) >
                      DateHelper.Day * eventData.timespan
                    : true) ||
                !FBConditionalEventData[eventIDs[i]].condition(
                    state,
                    eventData
                ) ||
                (trackOnce && state.playerState.FBEvents.includes(eventIDs[i]))
            )
                continue;

            // Default tracking
            if (
                !eventData.trackingMethod ||
                eventData.trackingMethod === FBTrackingMethod.DEFAULT ||
                eventData.trackingMethod === FBTrackingMethod.ALL
            ) {
                Game.sdk.gameTracking(eventIDs[i]);
                state = PlayerStateHelper.addFBTrackingEvent(
                    state,
                    eventIDs[i]
                );
            }

            // Pixel tracking
            if (
                eventData.trackingMethod === FBTrackingMethod.PIXEL ||
                eventData.trackingMethod === FBTrackingMethod.ALL
            ) {
                FBPixel.track(`Pixel_SOFA_${eventIDs[i]}`);
            }
        }

        return state;
    }
}
